import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthUserState } from "../context/authUser.context";
import { NIL_UUID } from "../apis/institute/wireInstituteMappers";
import { useInstituteService } from "../apis/institute/useInstituteService";
import {
  TInstitute,
  TCreateInstituteRequest,
  TUpdateInstituteRequest,
  TAddress,
  TCreateAddressRequest,
  TUpdateAddressRequest,
  TOnboardInstituteRequest,
} from "../apis/institute/institute.interfaces";

export const useInstituteHook = () => {
  const { getInstitutes } = useInstituteService();

  return useQuery<TInstitute[]>({
    queryKey: ["institutes"],
    queryFn: () => getInstitutes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateInstituteHook = () => {
  const { createInstituteLegacy } = useInstituteService();
  const { userDetails } = useAuthUserState();
  const queryClient = useQueryClient();

  return useMutation<TInstitute, Error, TCreateInstituteRequest>({
    mutationFn: (request: TCreateInstituteRequest) =>
      createInstituteLegacy(request, userDetails?.id ?? NIL_UUID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["institutes"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

export const useInstituteByIdHook = (id: string | undefined) => {
  const { getInstituteById } = useInstituteService();

  return useQuery<TInstitute, Error>({
    queryKey: ["institute", id],
    queryFn: () => {
      if (!id) {
        throw new Error("Institute ID is required");
      }
      return getInstituteById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateInstituteHook = () => {
  const { updateInstitute } = useInstituteService();
  const queryClient = useQueryClient();

  return useMutation<
    TInstitute,
    Error,
    { id: string; request: TUpdateInstituteRequest }
  >({
    mutationFn: ({ id, request }) => updateInstitute(id, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["institutes"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["institute", data.id] });
    },
  });
};

export const useAddressesHook = (instituteId: string | undefined) => {
  const { getAddresses } = useInstituteService();

  return useQuery<TAddress[], Error>({
    queryKey: ["addresses", instituteId],
    queryFn: () => {
      if (!instituteId) {
        throw new Error("Institute ID is required");
      }
      return getAddresses(instituteId);
    },
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateAddressHook = () => {
  const { createAddress } = useInstituteService();
  const queryClient = useQueryClient();

  return useMutation<TAddress, Error, TCreateAddressRequest>({
    mutationFn: (request) => createAddress(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["addresses", data.instituteId],
      });
      queryClient.invalidateQueries({
        queryKey: ["institute", data.instituteId],
      });
    },
  });
};

export const useUpdateAddressHook = () => {
  const { updateAddress } = useInstituteService();
  const queryClient = useQueryClient();

  return useMutation<
    TAddress,
    Error,
    { instituteId: string; addressId: string; request: TUpdateAddressRequest }
  >({
    mutationFn: ({ instituteId, addressId, request }) =>
      updateAddress(instituteId, addressId, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["addresses", data.instituteId],
      });
      queryClient.invalidateQueries({
        queryKey: ["institute", data.instituteId],
      });
    },
  });
};

export const useDeleteAddressHook = () => {
  const { deleteAddress } = useInstituteService();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { instituteId: string; addressId: string }>({
    mutationFn: ({ instituteId, addressId }) =>
      deleteAddress(instituteId, addressId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["addresses", variables.instituteId],
      });
      queryClient.invalidateQueries({
        queryKey: ["institute", variables.instituteId],
      });
    },
  });
};

export const useArchiveInstituteHook = () => {
  const { archiveInstitute } = useInstituteService();
  const queryClient = useQueryClient();

  return useMutation<TInstitute, Error, string>({
    mutationFn: (id) => archiveInstitute(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["institutes"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

export const useOnboardInstituteHook = () => {
  const { onboardInstitute } = useInstituteService();
  const { userDetails } = useAuthUserState();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: TOnboardInstituteRequest) =>
      onboardInstitute(request, userDetails?.id ?? NIL_UUID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["institutes"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};
